import { useState, type ReactNode } from "react";

type FaqItem = { q: string; a: ReactNode };

// Static FAQ copy — verbatim from the practice's FAQ document. Lives with the
// component (like the testimonials/areas-of-support lists in App.tsx); there is
// no CMS for these. Q8 turns the "contact form" reference and the email into
// links; the phone number stays plain text (a text is preferred).
export default function Faq({
  onNavigate,
}: {
  onNavigate?: (hash: string) => void;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const linkClass =
    "text-sage-deep underline underline-offset-2 transition-colors hover:text-sage";

  const faqs: FaqItem[] = [
    {
      q: "What are the benefits of therapy?",
      a: (
        <>
          <p>
            Being seen and truly understood by another human being will ease
            feelings of isolation. We are wired for relationship with other
            people, and we shouldn’t underestimate the distress experienced if
            we feel alone.
          </p>
          <p>
            The therapist will help you to honestly explore and untangle your
            problems. You will gain a deeper understanding of who you are and how
            you would like life to be.
          </p>
          <p>
            Improving your relationship with yourself improves your relationship
            with others. Self-acceptance enhances self-esteem and leads to
            healthy choices. Day-to-day functioning will become easier and our
            overall health improves.
          </p>
        </>
      ),
    },
    {
      q: "How many sessions do I need?",
      a: (
        <>
          <p>
            You may choose to have brief intervention counselling for a specific
            problem or reaction to a life event.
          </p>
          <p>
            Long term psychotherapy will perhaps go deeper into self-exploration
            or problems you have had for a long time.
          </p>
          <p>
            Therapy is an individual experience and ultimately the number of
            sessions is led by you. Sometimes clients stop for a while and then
            return for deeper work or to ‘top up’. A monthly check-in can also be
            beneficial after long-term work.
          </p>
        </>
      ),
    },
    {
      q: "How often are the sessions?",
      a: (
        <p>
          It is helpful to both of us if sessions are consistent. We will try for
          weekly appointments at the same time, if possible. Left much longer,
          things can become fragmented and your process disrupted. Sometimes
          fortnightly sessions may feel appropriate eventually.
        </p>
      ),
    },
    {
      q: "Where do I start?",
      a: (
        <p>
          Pick out counsellors that resonate with you and book initial
          consultations / chats with them. See how you feel, you are under no
          obligation to proceed. Feeling nervous is normal. If you decide to go
          ahead, you can then send me an email requesting a time and day that you
          could commit to. I will then give you all the detail you need for
          payment and where to find me etc.
        </p>
      ),
    },
    {
      q: "What is the cost and what if I need to cancel?",
      a: (
        <>
          <p>
            The cost is £60 per session. If you are suffering financial hardship,
            please talk to me about it and we can try to come to a more
            cost-effective arrangement.
          </p>
          <p>
            I will refund or transfer the fee as long as 24 hours’ notice is
            given. Please note that payment is required at least 24 hours in
            advance of the session.
          </p>
        </>
      ),
    },
    {
      q: "Is it confidential?",
      a: (
        <p>
          Yes, I work within the guidelines of the British Association for
          Counselling and Psychotherapy. However, I must break confidentiality if
          I have concerns for the safety of yourself or others. This would be
          discussed with you first wherever possible.
        </p>
      ),
    },
    {
      q: "Where are you?",
      a: (
        <p>
          I am based in Lymington but mainly work out of counselling rooms in
          Highcliffe (New Horizons Network) and Southampton (very close to
          Bedford Place). I am also happy to see clients online.
        </p>
      ),
    },
    {
      q: "What is the best way to make contact initially?",
      a: (
        <>
          <p>
            There is a{" "}
            <button
              type="button"
              onClick={() => onNavigate?.("#contact")}
              className={linkClass}
            >
              contact form at the end of this website
            </button>
            .
          </p>
          <p>
            You can also email me at{" "}
            <a href="mailto:newdawncounselling.info@gmail.com" className={linkClass}>
              newdawncounselling.info@gmail.com
            </a>{" "}
            and I will reply as soon as possible (remember to check your spam for
            a reply).
          </p>
          <p>My telephone number is 07592 155570, a text is preferable.</p>
        </>
      ),
    },
  ];

  return (
    <section id="faq" className="bg-[#f9fafb] px-6 py-16 md:px-10 md:py-24">
      <div className="mx-auto w-full max-w-3xl">
        {/* heading */}
        <div className="space-y-4 text-center">
          <p className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-sage-deep">
            Questions
          </p>
          <h2 className="text-4xl md:text-5xl font-trirong_light text-ink">
            Frequently asked…
          </h2>
        </div>

        {/* accordion */}
        <div className="mt-12 space-y-4 md:mt-16">
          {faqs.map((item, i) => {
            const isOpen = openIndex === i;
            const buttonId = `faq-q-${i}`;
            const panelId = `faq-a-${i}`;
            return (
              <div key={i} className="rounded-2xl bg-white shadow-card">
                <button
                  id={buttonId}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="flex w-full items-start justify-between gap-4 p-6 text-left md:p-8"
                >
                  <span className="text-lg md:text-xl font-trirong_reg leading-snug text-ink">
                    {item.q}
                  </span>
                  <svg
                    aria-hidden="true"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#7e957f"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`mt-1 flex-none transition-transform duration-200 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {isOpen && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="-mt-1 space-y-4 px-6 pb-6 text-[15px] leading-relaxed font-trirong_light text-ink-soft md:px-8 md:pb-8"
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
